// ============================================================================
// Mobile navigation (Option D, PR-2). Replaces the old horizontal 18–23 chip
// scroll with a native-app pattern:
//   • a fixed 5-slot bottom tab bar (Home · role-slot · Projects · My Work · More)
//     — item #650 / TCSD-0366 ruling 6: Ask AI left the bar for the pinned bar on
//     Home, and Projects took its slot. A role the Projects page is not open to
//     keeps Ask AI in that slot, so its bar is never a dead tab.
//   • a "More" bottom-sheet carrying the full grouped nav + a search field
//
// Touch targets are ≥44px. The role slot is My Route for field roles and
// Dashboard for office roles. The sheet search is palette-lite — it filters
// the listed destinations; the full ⌘K palette lands in PR-3 and will reuse
// the same nav-config index. Desktop is untouched (everything is md:hidden).
// ============================================================================

import { isFieldSeat } from './role-gates';
import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  FolderKanban,
  Gauge,
  Home as HomeIcon,
  Inbox,
  LogOut,
  MoreHorizontal,
  Route as RouteIcon,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { UserRole } from './types';
import {
  NAV,
  visibleHubChildren,
  leafVisible,
  hubVisible,
  hubIndexVisible,
  type NavGates,
  type NavLeaf,
  type BadgeKey,
} from './nav-config';
import type { NavBadgeCounts } from './use-nav-badges';
import { overlayClickClose } from './overlay-close';

interface SheetItem {
  label: string;
  path: string;
  icon: LucideIcon;
  badge?: number;
}

interface SheetGroup {
  label: string;
  items: SheetItem[];
}

export function MobileNav({
  role,
  gates,
  badges,
  onSignOut,
}: {
  role: UserRole | undefined;
  gates?: NavGates;
  badges: NavBadgeCounts;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const badgeOf = (key?: BadgeKey) => (key ? badges[key] : 0);

  // ----- Build the grouped sheet from the nav-config tree -----------------
  const groups = useMemo<SheetGroup[]>(() => {
    const out: SheetGroup[] = [];
    const core: SheetItem[] = [];
    const ops: SheetItem[] = [];

    let seenFirstDivider = false;
    for (const node of NAV) {
      if (node.kind === 'divider') {
        seenFirstDivider = true;
        continue;
      }
      if (node.kind === 'leaf') {
        if (!leafVisible(role, node, gates)) continue;
        const item: SheetItem = { label: node.label, path: node.path, icon: node.icon, badge: badgeOf(node.badgeKey) };
        if (seenFirstDivider) ops.push(item);
        else core.push(item);
      } else if (node.kind === 'hub') {
        if (!hubVisible(role, node, gates)) continue;
        // A hub with its own first tab (People's Desk, item 818) lists it for the
        // roles that hold it and no overview for anyone.
        const indexItem: SheetItem[] = node.index
          ? hubIndexVisible(role, node)
            ? [{ label: `${node.label} ${node.index.label}`, path: node.basePath, icon: node.icon }]
            : []
          : [{ label: `${node.label} overview`, path: node.basePath, icon: node.icon }];
        const items: SheetItem[] = [
          ...indexItem,
          ...visibleHubChildren(role, node, gates).map((c: NavLeaf) => ({
            label: c.label,
            path: c.path,
            icon: c.icon,
            badge: badgeOf(c.badgeKey),
          })),
        ];
        out.push({ label: node.label, items });
      }
    }

    const result: SheetGroup[] = [];
    if (core.length) result.push({ label: 'Workspace', items: core });
    if (ops.length) result.push({ label: 'Operations', items: ops });
    result.push(...out);
    return result;
  }, [role, gates, badges]);

  // ----- Search filter (palette-lite) -------------------------------------
  const filtered = useMemo<SheetGroup[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({ ...g, items: g.items.filter((i) => i.label.toLowerCase().includes(q)) }))
      .filter((g) => g.items.length > 0);
  }, [groups, query]);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  // ----- Bottom tab bar slots ---------------------------------------------
  const isField = isFieldSeat(role);
  const roleSlot: { to: string; label: string; icon: LucideIcon } = isField
    ? { to: '/route', label: 'My Route', icon: RouteIcon }
    : { to: '/dashboard', label: 'Dashboard', icon: Gauge };
  // The Projects tab follows the Projects page's own role list in nav-config —
  // one list, not a second copy here.
  const projectsLeaf = NAV.find((n): n is NavLeaf => n.kind === 'leaf' && n.path === '/project-work');
  const showProjects = !!projectsLeaf && leafVisible(role, projectsLeaf, gates);

  return (
    <>
      {/* ---- More bottom-sheet (overlay) ---- */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="More navigation">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-tcs-scrim/60"
            {...overlayClickClose(close)}
          />
          <div className="absolute inset-x-0 bottom-0 flex max-h-[82%] flex-col rounded-t-2xl border-t border-tcs-border bg-tcs-bg pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl">
            <div className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-tcs-border-strong" />
            <div className="flex items-center gap-2 px-4 pt-3">
              <div className="flex flex-1 items-center gap-2 rounded-lg border border-tcs-border bg-tcs-bg-muted px-3 py-2.5">
                <Search className="h-4 w-4 text-tcs-text-muted" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search modules…"
                  className="w-full bg-transparent text-sm text-tcs-text placeholder:text-tcs-text-muted focus:outline-none"
                />
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="grid h-9 w-9 shrink-0 place-items-center rounded-md text-tcs-text-secondary hover:bg-tcs-bg-muted"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2 pt-1">
              {filtered.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-tcs-text-muted">No matches.</div>
              )}
              {filtered.map((g) => (
                <div key={g.label}>
                  <div className="px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-[0.08em] text-tcs-text-muted">
                    {g.label}
                  </div>
                  {g.items.map((i) => (
                    <NavLink
                      key={i.path}
                      to={i.path}
                      end={i.path === '/'}
                      onClick={close}
                      className={({ isActive }) =>
                        [
                          'flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                          isActive
                            ? 'bg-tcs-surface font-medium text-tcs-text'
                            : 'text-tcs-text-secondary active:bg-tcs-surface',
                        ].join(' ')
                      }
                    >
                      <i.icon className="h-[18px] w-[18px] shrink-0 text-tcs-text-secondary" />
                      <span className="flex-1 truncate">{i.label}</span>
                      {typeof i.badge === 'number' && i.badge > 0 && (
                        <span className="rounded-full bg-st-warn-bg px-1.5 py-0.5 text-[10px] font-semibold leading-none text-st-warn-fg">
                          {i.badge}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              ))}
              {/* Account */}
              <div className="px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-[0.08em] text-tcs-text-muted">
                Account
              </div>
              <button
                type="button"
                onClick={() => {
                  close();
                  onSignOut();
                }}
                className="flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-tcs-text-secondary active:bg-tcs-surface"
              >
                <LogOut className="h-[18px] w-[18px] shrink-0 text-tcs-text-secondary" />
                <span className="flex-1 truncate text-left">Sign out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Bottom tab bar ---- */}
      <nav className="z-30 flex shrink-0 items-stretch border-t border-tcs-border bg-tcs-bg pb-[env(safe-area-inset-bottom)] md:hidden">
        <TabSlot to="/" label="Home" icon={HomeIcon} end />
        <TabSlot to={roleSlot.to} label={roleSlot.label} icon={roleSlot.icon} />
        {showProjects ? (
          <TabSlot to="/project-work" label="Projects" icon={FolderKanban} />
        ) : (
          <TabSlot to="/copilot" label="Ask AI" icon={Sparkles} emphasized />
        )}
        <TabSlot to="/my-work" label="My Work" icon={Inbox} badge={badges.myWork} />
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="More"
          aria-expanded={open}
          className={[
            'flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] transition-colors',
            open ? 'text-tcs-text' : 'text-tcs-text-muted',
          ].join(' ')}
        >
          <MoreHorizontal className="h-[22px] w-[22px]" />
          <span>More</span>
        </button>
      </nav>
    </>
  );
}

function TabSlot({
  to,
  label,
  icon: Icon,
  end,
  badge,
  emphasized,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  badge?: number;
  emphasized?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          'relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] transition-colors',
          isActive ? 'text-tcs-text' : 'text-tcs-text-muted',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={[
              'grid h-[26px] w-[26px] place-items-center rounded-lg',
              emphasized
                ? 'bg-tcs-accent-ink text-tcs-accent-on'
                : isActive
                  ? 'bg-tcs-surface text-tcs-text'
                  : 'text-tcs-text-muted',
            ].join(' ')}
          >
            <Icon className="h-[18px] w-[18px]" />
          </span>
          <span>{label}</span>
          {typeof badge === 'number' && badge > 0 && (
            <span className="absolute right-[18%] top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-st-warn-solid px-1 text-[9px] font-bold leading-none text-st-solid-ink">
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
